import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { TemplateActions } from '../../../../admin/containers/templates/TemplateCategory';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { IErrorMessageComponentProps } from '../../../../models/AppState';
import { getPlainTextPreviewForRawRichTextContent, isTemplateNew } from '../../../../models/UiUtils';
import { ITemplateCard } from '../../../../viewmodels/AppViewModels';
import { baseStyleSheet, bs } from '../../../styles/styles';
import { IMoreMenuItem } from '../../MoreMenu';
import '../../richContent/RichContentDocumentEditor/placeholders.tinymce.css';

import { useLocation } from 'react-router';
import { ConditionalAccess } from '../../../../admin/components/ConditionalAccess';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useLambda } from '../../../../models/hooks/useLambda';
import { AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { Button } from '../../../components-v2/Button';
import { THEME } from '../../../components-v2/Theme/theme';
import { ButtonBase } from '../../Button';
import { ExpiryIcon } from '../../svgs/icons/ExpiryIcon';
import { OpenLetter } from '../../svgs/icons/OpenLetter';
import { SendNow } from '../../svgs/icons/SendNow';
import { SocialMediaIndicatorIcon } from '../../svgs/icons/SocialMediaIndicatorIcon';
import {
	CampaignSummaryFromContentShort,
	CampaignTemplateCardMenu,
} from '../../templateCards/CampaignTemplateCard/presentation';
import { getTemplateApprovalStatus } from '../../TemplateReviewStatusIndicator';
import { PopoverType, TinyPopover } from '../../TinyPopover';
import { SimpleTooltip, Tooltip, TooltipContent, TooltipTrigger } from '../../Tooltip';
import { s as tooltipStyles } from '../../Tooltip/styles';
import { CampaignExpiryHint } from '../CampaignExpiryHint';
import { campaignTemplateCardStyleSheet, styleSheet } from './styles';

export interface CampaignCalendarTemplateCardProps extends IImpersonationContextComponentProps {
	card: ITemplateCard;
	ctaText?: string;
	className?: string;
	copyMenu?: React.ReactNode;
	industry?: Api.Industry;
	isAccountTemplate?: boolean;
	isFeatured?: boolean;
	isBrokerDealer?: boolean;
	onCtaClicked?(e?: React.MouseEvent<Element>): void;
	onDelete?(): void;
	onShare?(isShared: boolean): void;
	onSocialClicked?(e: React.MouseEvent<Element>): void;
	rowIndex?: number;
	styles?: StyleDeclarationValue[];
	templateIndex?: number;
	useWholeCardAsCTA?: boolean;
}

const CampaignCalendarTemplateCardBase = ({
	card,
	copyMenu,
	impersonationContext,
	isAccountTemplate,
	isFeatured,
	onCtaClicked,
	onDelete,
	onShare,
	onSocialClicked,
	rowIndex,
	styles,
	templateIndex,
	useWholeCardAsCTA,
}: CampaignCalendarTemplateCardProps) => {
	const userSession = useUserSession();
	const loc = useLocation();
	const [showSocialPopover, , setShowSocialPopover] = useLambda(false);
	const expirationDate = card?.schedule?.expirationDate ? moment(card?.schedule?.expirationDate) : undefined;
	// intentionally disabling social media indicator while under impersonation context until we have compliance figured out
	const containsSocialMedia =
		userSession?.account?.features?.socialMedia?.enabled && card?.associatedTemplates?.length > 0;
	const showingSocialMedia = !impersonationContext?.isValid && !loc.pathname?.includes('ghost') && containsSocialMedia;

	const onSocialMediaIndicatorClicked = (e: React.MouseEvent<SVGSVGElement>) => {
		if (useWholeCardAsCTA) {
			return;
		}
		setShowSocialPopover(false)();
		onSocialClicked?.(e);
	};

	const isNew = isTemplateNew(card);

	const approvalStatus = getTemplateApprovalStatus(card?.templateReview, false);

	return (
		<div
			className={css(
				bs.relative,
				campaignTemplateCardStyleSheet.container,
				isFeatured && campaignTemplateCardStyleSheet.featured,
				campaignTemplateCardStyleSheet.smallContainer,
				...(styles || [])
			)}
			onClick={useWholeCardAsCTA ? onCtaClicked : undefined}
		>
			{approvalStatus}

			<ConditionalAccess permission={AdminPermission.CreateCampaigns}>{copyMenu}</ConditionalAccess>
			{rowIndex === 0 && isFeatured && templateIndex === 0 && (
				<SendNow className={css(campaignTemplateCardStyleSheet.sendNow)} />
			)}
			{isAccountTemplate ? (
				<CampaignTemplateCardMenu card={card} userSession={userSession} onDelete={onDelete} onShare={onShare} />
			) : null}
			{rowIndex === 0 && isFeatured && templateIndex === 2 && (
				<OpenLetter className={css(campaignTemplateCardStyleSheet.openLetter)} />
			)}
			<div className={css(campaignTemplateCardStyleSheet.headerContainer)}>
				<div
					className={css(
						campaignTemplateCardStyleSheet.nameContainer,
						campaignTemplateCardStyleSheet.nameContainerSmall,
						isFeatured && rowIndex === 0 ? campaignTemplateCardStyleSheet.flagHighlight : undefined
					)}
				>
					<Tooltip>
						<TooltipTrigger asChild>
							<ButtonBase className={css(styleSheet.textContentTwoLines, bs.cursorPointer)} onClick={onCtaClicked}>
								{card.name || 'Custom Post'}{' '}
							</ButtonBase>
						</TooltipTrigger>
						<TooltipContent styles={[styleSheet.tooltipContent]}>{card.name ?? 'Custom Post'}</TooltipContent>
					</Tooltip>
					{expirationDate ? <CampaignExpiryHint expirationDate={expirationDate} /> : null}
					{isNew ? <span className={css(campaignTemplateCardStyleSheet.newItem)}>(New!)</span> : null}
				</div>
				{showingSocialMedia ? (
					<TinyPopover
						align='end'
						anchor={
							<SocialMediaIndicatorIcon
								className={css(bs.cursorPointer)}
								onClick={onSocialMediaIndicatorClicked}
								onMouseEnter={setShowSocialPopover(true)}
								onMouseLeave={setShowSocialPopover(false)}
							/>
						}
						anchorStyles={[bs.absolute, campaignTemplateCardStyleSheet.popoverAnchor]}
						dismissOnOutsideAction={true}
						isOpen={showSocialPopover}
						placement={['top', 'left', 'bottom']}
						type={PopoverType.lightBlue}
					>
						<div className={css(campaignTemplateCardStyleSheet.popover)}>Also available for social media. </div>
					</TinyPopover>
				) : (
					<div className={css(campaignTemplateCardStyleSheet.popoverContainer)} />
				)}
			</div>

			<CampaignSummaryFromContentShort card={card} />
		</div>
	);
};

export const CampaignCalendarTemplateCard = inject(ImpersonationContextKey)(CampaignCalendarTemplateCardBase);

export const CampaignCalendarTemplateCardCta = ({
	ctaText,
	onCtaClicked,
	useWholeCardAsCTA,
}: {
	ctaText: string;
	onCtaClicked: () => void;
	useWholeCardAsCTA: boolean;
	isBrokerDealer?: boolean;
	card: ITemplateCard;
}) => {
	if (!onCtaClicked && useWholeCardAsCTA) {
		return null;
	}

	return (
		<div className={css(baseStyleSheet.horizontalStack, bs.mt5, bs.itemsCenter)}>
			<Button variant='secondary' size='sm' onClick={onCtaClicked}>
				<span>{ctaText}</span>
			</Button>
		</div>
	);
};

interface SocialCampaignCalendarTemplateCardProps
	extends IImpersonationContextComponentProps,
		IErrorMessageComponentProps {
	card: ITemplateCard | Api.ISocialMediaPost;
	className?: string;
	ctaText?: string;
	isFeatured?: boolean;
	onCtaClicked?(): void;
	onMenuItemClicked?(menuItem: IMoreMenuItem<TemplateActions>, e: React.MouseEvent<HTMLElement>): void;
}

const SocialCampaignCalendarTemplateCardBase: React.FC<SocialCampaignCalendarTemplateCardProps> = props => {
	const { card, onCtaClicked, isFeatured } = props;

	const { text, truncated: _truncated } = getPlainTextPreviewForRawRichTextContent(card?.content, {
		maxCharCount: 100,
	});
	const fullText = getPlainTextPreviewForRawRichTextContent(card?.content, {
		maxCharCount: 1000,
	}).text;
	const tCard = card as ITemplateCard;
	const expirationDate = tCard?.schedule?.expirationDate ? moment(tCard?.schedule?.expirationDate) : null;
	const isNew = isTemplateNew(tCard);
	const approvalStatus = getTemplateApprovalStatus(tCard?.templateReview);

	return (
		<div className={css(styleSheet.modernCardContainer, isFeatured && styleSheet.modernCardFeatured)}>
			{(isNew || expirationDate) && (
				<div className={css(styleSheet.modernTopContainer)}>
					<div className={css(styleSheet.modernInfoContainer)}>
						{isNew && <span className={css(styleSheet.modernNewBadge)}>New!</span>}
						{expirationDate && (
							<Tooltip>
								<TooltipTrigger>
									<ExpiryIcon fill={THEME.colors.hover.main} />
								</TooltipTrigger>
								<TooltipContent styles={[styleSheet.modernTooltipContent]}>
									<span>
										{`This is a time-sensitive campaign. Posts will not be published after ${expirationDate.format('MMM D, YYYY')}.`}
									</span>
								</TooltipContent>
							</Tooltip>
						)}
					</div>
				</div>
			)}

			{/* Header with title */}
			<div className={css(styleSheet.modernHeaderContainer)}>
				<div className={css(styleSheet.modernTitleContainer)}>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button variant='link' styles={[styleSheet.modernTitleButton]} onClick={onCtaClicked}>
								{card.name || 'Custom Social Post'}
							</Button>
						</TooltipTrigger>
						<TooltipContent styles={[styleSheet.modernTooltipContent]}>
							{card.name ?? 'Custom Social Post'}
						</TooltipContent>
					</Tooltip>
				</div>
			</div>

			{/* Content summary */}
			{text && (
				<div className={css(styleSheet.modernContentContainer)}>
					<SimpleTooltip title={fullText !== text ? fullText : ''} styles={[tooltipStyles.contentWithMaxWidth]}>
						<p className={css(styleSheet.modernContentText)}>
							<span>{text}</span>
						</p>
					</SimpleTooltip>
				</div>
			)}

			{/* Approval status */}
			{approvalStatus}
		</div>
	);
};

export const SocialCampaignCalendarTemplateCard = inject(ImpersonationContextKey)(
	SocialCampaignCalendarTemplateCardBase
);

const BlogCampaignCalendarTemplateCardBase: React.FC<SocialCampaignCalendarTemplateCardProps> = props => {
	const { card, onCtaClicked, isFeatured } = props;
	const tCard = card as ITemplateCard;
	const expirationDate = tCard?.schedule?.expirationDate ? moment(tCard?.schedule?.expirationDate) : null;
	const isNew = isTemplateNew(tCard);
	const approvalStatus = getTemplateApprovalStatus(tCard?.templateReview);

	const { text, truncated: _truncated } = getPlainTextPreviewForRawRichTextContent(card?.content, {
		maxCharCount: 100,
	});
	const fullText = getPlainTextPreviewForRawRichTextContent(card?.content, {
		maxCharCount: 1000,
	}).text;

	return (
		<div className={css(styleSheet.modernCardContainer, isFeatured && styleSheet.modernCardFeatured)}>
			{(isNew || expirationDate) && (
				<div className={css(styleSheet.modernTopContainer)}>
					<div className={css(styleSheet.modernInfoContainer)}>
						{isNew && <span className={css(styleSheet.modernNewBadge)}>New!</span>}
						{expirationDate && (
							<Tooltip>
								<TooltipTrigger>
									<div className={css(styleSheet.modernInfoIcon)}>
										<svg width='16' height='16' viewBox='0 0 24 24' fill='currentColor'>
											<path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z' />
										</svg>
									</div>
								</TooltipTrigger>
								<TooltipContent styles={[styleSheet.modernTooltipContent]}>
									<span>
										{`This is a time-sensitive campaign. Posts will not be published after ${expirationDate.format('MMM D, YYYY')}.`}
									</span>
								</TooltipContent>
							</Tooltip>
						)}
					</div>
				</div>
			)}

			{/* Header with title */}
			<div className={css(styleSheet.modernHeaderContainer)}>
				<div className={css(styleSheet.modernTitleContainer)}>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button variant='link' styles={[styleSheet.modernTitleButton]} onClick={onCtaClicked}>
								{card.name || 'Custom Blog Post'}
							</Button>
						</TooltipTrigger>
						<TooltipContent styles={[styleSheet.modernTooltipContent]}>
							{card.name ?? 'Custom Blog Post'}
						</TooltipContent>
					</Tooltip>
				</div>
			</div>

			{/* Content summary */}
			{text && (
				<div className={css(styleSheet.modernContentContainer)}>
					<SimpleTooltip title={fullText !== text ? fullText : ''} styles={[tooltipStyles.contentWithMaxWidth]}>
						<p className={css(styleSheet.modernContentText)}>
							<span>{text}</span>
						</p>
					</SimpleTooltip>
				</div>
			)}

			{/* Approval status */}
			{approvalStatus}
		</div>
	);
};

export const BlogCampaignCalendarTemplateCard = inject(ImpersonationContextKey)(BlogCampaignCalendarTemplateCardBase);
